import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs/operators';
import * as _ from 'lodash';
import { AssessRequestRaService } from '../../assess-request-ra/assess-request-ra.service';
import { GLOBAL_RA_ROLE_ID, RA_ROLE_ID, SUPER_ADMIN_ROLE_ID } from '../../common/constants/role-ids';
import { DataService } from '../../common/services/data.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-clone-and-edit-rule',
  templateUrl: './clone-and-edit-rule.component.html',
  styleUrls: ['./clone-and-edit-rule.component.scss']
})
export class CloneAndEditRuleComponent implements OnInit {
  rule = {
    ruleId: '',
    ruleVersion: '',
    ruleType: '',
    ruleTypeID: '',
    ruleStatus: ''
  };
  markets = [];
  selectedMarket: any;
  selectedRpcPath: any;
  rpcPathResults = [];
  rpcLevels = ['', '', '', '', ''];
  todayDate: Date = new Date();
  validityStartDate: Date = new Date();
  validityEndDate = new Date('9999-12-31');
  loading = false;
  loadingMsg = 'Fetching data from FAST Rule-Engine';
  private pendingRequests = 0;

  constructor(
    private dataService: DataService,
    private apiService: AssessRequestRaService,
    private route: ActivatedRoute,
    private router: Router,
    private matSnackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    const routeParams = this.route.snapshot.paramMap;
    const queryParams = this.route.snapshot.queryParamMap;
    const queryRuleId = routeParams.get('ruleId') || queryParams.get('ruleId');
    const queryRuleTypeID = routeParams.get('ruleTypeID') || queryParams.get('ruleTypeID');
    const queryRuleVersion = routeParams.get('ruleVersion') || queryParams.get('ruleVersion');
    const queryRuleStatus = routeParams.get('ruleStatus') || queryParams.get('ruleStatus');

    const queryRule = queryRuleId && queryRuleTypeID ? {
      ruleId: queryRuleId,
      ruleTypeID: queryRuleTypeID,
      ruleType: '',
      ruleVersion: queryRuleVersion || '',
      ruleStatus: queryRuleStatus || ''
    } : null;

    if (queryRule) {
      this.bindSelectedRule(queryRule);
    }

    this.dataService.getViewruleToEdit().pipe(take(1)).subscribe((rowData) => {
      if (rowData) {
        this.bindSelectedRule(rowData);
      }
    });

    if (!this.rule.ruleType && this.rule.ruleTypeID) {
      this.loadRuleTypes(Number(this.rule.ruleTypeID));
    }

    this.fetchRuleDetailsIfNeeded();

    this.loadMarkets();
  }

  private startLoading(): void {
    this.pendingRequests++;
    this.loadingMsg = 'Fetching data from FAST Rule-Engine';
    this.loading = true;
  }

  private stopLoading(): void {
    this.pendingRequests = Math.max(0, this.pendingRequests - 1);
    this.loading = this.pendingRequests > 0;
  }

  private loadMarkets(): void {
    const userDetails = JSON.parse(localStorage.getItem('userRolesPermissions'));
    const roles = userDetails ? userDetails.roles : [];
    const isSuperAdminOrGlobalRA = roles.some(role =>
      role.roleId === SUPER_ADMIN_ROLE_ID || role.roleId === GLOBAL_RA_ROLE_ID ||
      (role.roleId === RA_ROLE_ID && role.market === null));
    const userBasedMarkets = _.uniqBy(
      roles.filter(role => role.roleId === RA_ROLE_ID && role.market !== null)
        .map(role => role.market)
        .sort((a, b) => (a.marketName > b.marketName) ? 1 : -1),
      market => [market.marketId, market.marketName].join()
    );
    const apiUrl = environment.APIFAsT + '/allmarkets';
    this.startLoading();
    this.apiService.getData(apiUrl).pipe(take(1)).subscribe((markets) => {
      this.markets = isSuperAdminOrGlobalRA ? (markets || []) : userBasedMarkets;
      this.stopLoading();
    }, () => {
      this.stopLoading();
    });
  }

  private loadRuleTypes(ruleTypeID: number): void {
    const apiUrl = environment.APIFAsTRule + '/rules/ruleTypes';
    this.startLoading();
    this.apiService.getData(apiUrl).pipe(take(1)).subscribe((ruleTypes) => {
      const matchedRuleType = (ruleTypes || []).find((rt: any) => rt.ruleTypeID === ruleTypeID);
      if (matchedRuleType && !this.rule.ruleType) {
        this.rule.ruleType = matchedRuleType.ruleTypeName || '';
      }
      this.stopLoading();
    }, () => {
      this.stopLoading();
    });
  }

  private formatRuleVersion(version: any): string {
    const value = String(version == null ? '' : version).trim();
    if (!value) { return ''; }
    return 'V' + value.replace(/^v/i, '');
  }

  private bindSelectedRule(rowData: any): void {
    const rawVersion = rowData.ruleVersion || rowData.originalRuleVersion || rowData.orginalRuleVersion || '';
    const formattedVersion = this.formatRuleVersion(rawVersion);
    this.rule = {
      ruleId: rowData.ruleId || rowData.ruleID || rowData.RuleId || rowData.RuleID || this.rule.ruleId || '',
      ruleVersion: formattedVersion || this.rule.ruleVersion || '',
      ruleType: rowData.ruleType || rowData.ruleTypeName || rowData.RuleType || this.rule.ruleType || '',
      ruleTypeID: rowData.ruleTypeID || rowData.ruleTypeId || rowData.RuleTypeID || rowData.RuleTypeId || this.rule.ruleTypeID || '',
      ruleStatus: rowData.ruleStatus != null ? rowData.ruleStatus : this.rule.ruleStatus || ''
    };

    this.selectedMarket = null;
    this.selectedRpcPath = null;
    this.rpcLevels = ['', '', '', '', ''];
  }

  private fetchRuleDetailsIfNeeded(): void {
    if (!this.rule.ruleType && this.rule.ruleId && this.rule.ruleTypeID) {
      const normalizedRuleId = String(this.rule.ruleId).trim();
      const normalizedRuleTypeId = String(this.rule.ruleTypeID).trim();
      let apiurl = environment.APIFAsTRule + '/rules/ruleID/' + normalizedRuleId + '/ruleType/' + normalizedRuleTypeId;
      const ruleVer = String(this.rule.ruleVersion || '').replace(/^V/i, '').trim();
      if (ruleVer) {
        apiurl += '/ruleVersion/' + ruleVer;
      }
      this.startLoading();
      this.apiService.getData(apiurl).pipe(take(1)).subscribe((response: any) => {
        if (response) {
          const ruleData = Array.isArray(response) ? response[0] : response;
          if (ruleData) {
            if (!this.rule.ruleType) {
              this.rule.ruleType = ruleData.ruleType || ruleData.ruleTypeName || ruleData.RuleType || '';
            }
            if (!this.rule.ruleVersion && (ruleData.ruleVersion || ruleData.originalRuleVersion || ruleData.orginalRuleVersion)) {
              this.rule.ruleVersion = this.formatRuleVersion(ruleData.ruleVersion || ruleData.originalRuleVersion || ruleData.orginalRuleVersion);
            }
          }
        }
        this.stopLoading();
      }, () => {
        this.stopLoading();
      });
    }
  }

  onMarketChange(): void {
    this.selectedRpcPath = null;
    this.rpcPathResults = [];
    this.rpcLevels = ['', '', '', '', ''];
  }

  searchRpcPath(event: any): void {
    const query = event.query || '';
    const apiUrl = environment.APIFAsT + '/searchtext/' + query + '/rpcpaths';
    this.startLoading();
    this.apiService.postData(apiUrl, [this.selectedMarket]).pipe(take(1)).subscribe((response) => {
      this.stopLoading();
      this.rpcPathResults = response || [];
    }, () => {
      this.stopLoading();
      this.rpcPathResults = [];
    });
  }

  onRpcPathSelected(event: any): void {
    const path = event.value || event;
    this.selectedRpcPath = path;
    if (!path || !path.rpcId) {
      this.rpcLevels = ['', '', '', '', ''];
      return;
    }

    const pathText = path.rpcSearchValue || path.rpcDescription || '';
    const selectedLevelCount = pathText.split('_').filter((level: string) => level.trim()).length;
    const apiUrl = environment.APIFAsT + '/rpcid/' + path.rpcId + '/parents';
    this.startLoading();
    this.apiService.getData(apiUrl).pipe(take(1)).subscribe((responseData) => {
      this.stopLoading();
      if (responseData !== null) {
        const apiLevels = responseData.map((rpcLevel: any) => rpcLevel.rpcDescription).filter(Boolean);
        if (apiLevels.length > 0) {
          const levelCount = selectedLevelCount || apiLevels.length;
          this.rpcLevels = apiLevels.slice(0, levelCount).concat(['', '', '', '', '']).slice(0, 5);
        }
      }
    }, () => {
      this.stopLoading();
    });
  }

  onStartDateSelect(event: any): void {
    if (this.validityStartDate && this.validityEndDate && new Date(this.validityStartDate) > new Date(this.validityEndDate)) {
      this.validityEndDate = new Date(this.validityStartDate);
    }
  }

  onEndDateSelect(event: any): void {
    if (this.validityStartDate && this.validityEndDate && new Date(this.validityEndDate) < new Date(this.validityStartDate)) {
      this.validityStartDate = new Date(this.validityEndDate);
    }
  }

  goToPref(): void {
    const url = environment.PREFADMIN;
    window.open(url, '_blank');
  }

  next(): void {
    if (this.loading || !this.selectedMarket || !this.selectedRpcPath || !this.selectedMarket.marketId || !this.selectedRpcPath.rpcId) {
      return;
    }

    this.startLoading();
    const request = {
      parentRuleId: this.rule.ruleId,
      marketId: this.selectedMarket.marketId,
      rpcId: this.selectedRpcPath.rpcId,
      ruleTypeId: this.rule.ruleTypeID
    };
    const apiUrl = environment.APIFAsTRule + '/duplicateRules';
    this.apiService.postData(apiUrl, request).pipe(take(1)).subscribe((response) => {
      this.stopLoading();
      if (!response || response.success !== true || (response.data !== null && !Array.isArray(response.data))) {
        this.matSnackBar.open('Unable to check for duplicate rules. Please try again.', 'close', {
          duration: 4000,
          panelClass: ['error'],
          verticalPosition: 'bottom'
        });
        return;
      }

      const duplicateRules = response.data;
      if (duplicateRules === null || duplicateRules.length === 0) {
        this.continueToEdit();
        return;
      }

      const ruleIds = duplicateRules.map((duplicate: any) => duplicate.ruleId || duplicate.ruleID || duplicate).join(', ');
      const ruleIdLabel = duplicateRules.length > 1 ? 'Rule Ids' : 'Rule Id';
      this.matSnackBar.open('Duplicate rule found with selected market: ' + this.selectedMarket.marketName +
        ', RPC: ' + (this.selectedRpcPath.rpcSearchValue || this.selectedRpcPath.rpcDescription) +
        '. ' + ruleIdLabel + ': ' + ruleIds, 'close', {
          duration: 4000,
          panelClass: ['error'],
          verticalPosition: 'bottom'
        });
    }, () => {
      this.stopLoading();
      this.matSnackBar.open('Unable to check for duplicate rules. Please try again.', 'close', {
        duration: 4000,
        panelClass: ['error'],
        verticalPosition: 'bottom'
      });
    });
  }

  private continueToEdit(): void {
    const validFrom = this.validityStartDate ? new Date(this.validityStartDate).toISOString() : '';
    const validTo = this.validityEndDate ? new Date(this.validityEndDate).toISOString() : '';

    const reqData = [{
      ruleId: this.rule.ruleId,
      ruleTypeID: this.rule.ruleTypeID,
      ruleVersion: String(this.rule.ruleVersion || '').replace(/^V/i, ''),
      ValidFrom: validFrom,
      ValidTo: validTo,
      AddRuleType: 'Clone',
      ruleStatus: this.rule.ruleStatus,
      MRPCs: [{
        marketId: this.selectedMarket.marketId,
        marketName: this.selectedMarket.marketName,
        RPCId: this.selectedRpcPath.rpcId,
        RPCName: this.selectedRpcPath.rpcDescription || this.selectedRpcPath.rpcSearchValue,
        RPCFullText: this.selectedRpcPath.rpcSearchValue || this.selectedRpcPath.rpcDescription
      }]
    }];
    const ruleDataToEdit = {
      ruleId: this.rule.ruleId,
      ruleTypeID: this.rule.ruleTypeID,
      ruleType: this.rule.ruleType,
      ruleVersion: String(this.rule.ruleVersion || '').replace(/^V/i, ''),
      ruleStatus: this.rule.ruleStatus,
      marketId: this.selectedMarket.marketId,
      marketName: this.selectedMarket.marketName,
      rpcId: this.selectedRpcPath.rpcId,
      rpcName: this.selectedRpcPath.rpcDescription || this.selectedRpcPath.rpcSearchValue,
      rpcFullText: this.selectedRpcPath.rpcSearchValue || this.selectedRpcPath.rpcDescription,
      validFrom: validFrom,
      validTo: validTo,
      isClone: true
    };
    this.dataService.setViewruleToEdit(ruleDataToEdit);
    this.router.navigate(['/rule-management/view-rules/edit-rules'], {
      queryParams: {
        mode: 'edit',
        reqData: JSON.stringify(reqData)
      }
    });
  }

}
